import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, List, ListItemButton, TextField } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import { styled, useTheme } from '@mui/material/styles';
import { useLazyQuery, DocumentNode } from '@apollo/client';
import validator from '../../../../lib/validator';

const CustomTextField = styled(TextField)(() => ({
  '& .MuiInputLabel-root': {
    fontSize: '1em',
  },
  '& .MuiInputBase-input': {
    fontSize: '1em',
  },
  '& .MuiInputBase-root': {
    fontSize: '1em',
    height: '38px',
  },
}));

const PaginatedListbox = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
  (listboxProps, ref) => (
    <List
      {...listboxProps}
      ref={ref}
      sx={{
        width: '100%',
        maxWidth: '100%',
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'auto',
        maxHeight: '300px',
        '& ul': { padding: 0 },
      }}>
      {listboxProps.children}
    </List>
  )
);
PaginatedListbox.displayName = 'PaginatedListbox';

type Option = { label: string; value: any };

type PaginatedResponse = {
  options: { pcId: string; fullName: string; tiId: string; tiName: string; pcIdeNum: string }[];
  totalCount: number;
  hasNextPage: boolean;
  currentPage: number;
};

type Props = {
  name: string;
  control: Control<any>;
  schema: validator.AnySchema;
  query: DocumentNode;
  queryName: string;
  variables?: Record<string, any>;
  pageSize?: number;
  label?: string;
  disabled?: boolean;
  borderBottom?: boolean;
  customOnChange?: (e?: ChangeEvent<HTMLInputElement>) => void;
  sx?: any;
  onBlur?: any;
  formatOption?: (item: any) => Option;
  defaultOption?: Option | null;
};

export const PaginatedAutocompleteAdapter = (props: Props) => {
  const {
    name,
    control,
    schema,
    query,
    queryName,
    variables = {},
    pageSize = 20,
    label,
    disabled,
    sx,
    onBlur,
    borderBottom = true,
    customOnChange,
    formatOption,
    defaultOption = null,
  } = props;

  const theme = useTheme();
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = useState<Option | null>(defaultOption);

  useEffect(() => {
    if (defaultOption && !selectedOption) {
      setSelectedOption(defaultOption);
    }
  }, [defaultOption]);

  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultFormatOption = (item: any): Option => ({
    label: item.fullName || '',
    value: item.pcId,
  });

  const formatFn = formatOption || defaultFormatOption;

  const [fetchData, { loading }] = useLazyQuery(query, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const result: PaginatedResponse = data?.[queryName];
      if (!result) return;

      const newOptions = result.options.map(formatFn);

      if (result.currentPage === 0) {
        setOptions(newOptions);
      } else {
        setOptions((prev) => [...prev, ...newOptions]);
      }

      setHasNextPage(result.hasNextPage);
      setPage(result.currentPage);
    },
  });

  const loadPage = useCallback(
    (pageNum: number, queryValue?: string) => {
      fetchData({
        variables: {
          ...variables,
          page: pageNum,
          pageSize,
          ...(queryValue ? { queryValue } : {}),
        },
      });
    },
    [fetchData, variables, pageSize]
  );

  const handleOpen = () => {
    setPage(0);
    setSearchValue('');
    loadPage(0);
  };

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listbox = event.currentTarget;
    const isNearBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 50;

    if (isNearBottom && hasNextPage && !loading) {
      loadPage(page + 1, searchValue || undefined);
    }
  };

  const handleInputChange = (_e: any, value: string, reason: string) => {
    if (reason !== 'input') return;
    setSearchValue(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setOptions([]);
      setPage(0);
      loadPage(0, value || undefined);
    }, 400);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate: (value) => schema.validateAt(name, { [name]: value }) }}
      render={({ field, fieldState }) => (
        <Autocomplete
          sx={{ ...sx }}
          size="small"
          disabled={disabled}
          onOpen={handleOpen}
          filterOptions={(x) => x}
          isOptionEqualToValue={(option: any, val: any) => option?.value === val?.value}
          value={selectedOption}
          options={
            selectedOption && !options.some((o) => o.value === selectedOption.value)
              ? [selectedOption, ...options]
              : options
          }
          loading={loading}
          getOptionLabel={(option: Option) => option?.label || ''}
          ListboxComponent={PaginatedListbox}
          ListboxProps={{ onScroll: handleScroll } as any}
          renderOption={(prop: any, option) => (
            <ListItemButton {...prop} key={option.value}>
              {option.label}
            </ListItemButton>
          )}
          onInputChange={handleInputChange}
          onChange={(_, selected: Option | null) => {
            setSelectedOption(selected);
            field.onChange(selected?.value || null);
            if (customOnChange)
              customOnChange({
                target: { name, value: selected?.value },
              } as any);
          }}
          onBlur={onBlur || field.onBlur}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              variant="outlined"
              label={label || 'Selecciona una opción'}
              error={Boolean(fieldState.error?.message)}
              helperText={`${fieldState.error?.message || ''}`}
              onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                const pasted = e.clipboardData.getData('text');
                const cleaned = pasted.replace(/[.\s]/g, '').replace(/,/g, '');
                if (/^\d+$/.test(cleaned) && cleaned !== pasted) {
                  e.preventDefault();
                  const input = e.target as HTMLInputElement;
                  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                    window.HTMLInputElement.prototype,
                    'value'
                  )?.set;
                  nativeInputValueSetter?.call(input, cleaned);
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                }
              }}
              sx={{
                mb: theme.spacing(1),
                fontSize: '1em',
                ...(!borderBottom ? { 'margin-bottom': '0px' } : {}),
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};
